import { GATES, GATE_LABELS } from "@/lib/utils-ros";
import { CheckCircle2, Circle } from "lucide-react";

/**
 * 5-gate lead/opportunity funnel with sharp chevron progression.
 */
export function GateFunnel({ currentGate, onGateClick, compact = false, variant = "default" }) {
  const idx = GATES.indexOf(currentGate);

  if (variant === "detail") {
    const completedColors = ["#B8DFFA", "#68B3F0", "#238FE4", "#0F6DBE", "#07539A"];
    return (
      <div className="overflow-x-auto pb-1" data-testid="gate-funnel">
        <div className="flex items-stretch min-w-[720px] gap-[3px]">
          {GATES.map((g, i) => {
            const done = i < idx;
            const active = i === idx;
            const background = active ? "#F5A23A" : done ? completedColors[i] : "#E2E8EC";
            const color = active || (done && i > 0) ? "#FFFFFF" : done ? "#07539A" : "#71828B";
            return (
              <button
                key={g}
                type="button"
                onClick={() => onGateClick?.(g)}
                data-testid={`gate-${g}`}
                aria-current={active ? "step" : undefined}
                className={`flex-1 relative h-[68px] ${i === 0 ? "gate-chevron-first" : "gate-chevron"} text-left px-4 transition-[filter,transform] hover:brightness-[0.98] focus:outline-none focus:ring-2 focus:ring-[#07539A]/30`}
                style={{
                  background,
                  color,
                  paddingLeft: i === 0 ? 18 : 28,
                  paddingRight: 22,
                  filter: active ? "drop-shadow(0 5px 8px rgba(245,162,58,0.22))" : undefined,
                }}
              >
                <span className="block text-[10px] uppercase tracking-widest font-semibold">Gate {i + 1}</span>
                <span className="block text-[11px] leading-tight mt-1 truncate">{GATE_LABELS[g]}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-stretch w-full gap-[2px]" data-testid="gate-funnel">
      {GATES.map((g, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <button
            key={g}
            onClick={() => onGateClick?.(g)}
            data-testid={`gate-${g}`}
            className={`flex-1 relative ${compact ? "h-9" : "h-11"} ${i === 0 ? "gate-chevron-first" : "gate-chevron"} transition-colors text-left px-3 group ${
              active
                ? "bg-[var(--copper)]/15 border border-[var(--copper)]/50"
                : done
                ? "bg-[var(--success)]/10 border border-[var(--success)]/25"
                : "bg-white/[0.02] border border-white/5 hover:bg-white/[0.05]"
            }`}
            style={{
              paddingLeft: i === 0 ? 12 : 22,
              paddingRight: 22,
            }}
          >
            <div className="flex items-center gap-2 h-full">
              {done ? (
                <CheckCircle2 size={12} className="text-[var(--success)] shrink-0" />
              ) : active ? (
                <div className="w-2 h-2 rounded-full bg-[var(--copper)] ai-pulse shrink-0" />
              ) : (
                <Circle size={12} className="text-[var(--text-dim)] shrink-0" strokeWidth={1.5} />
              )}
              <div className="min-w-0">
                <div className={`text-[9px] uppercase tracking-widest ${active ? "text-[var(--copper)]" : done ? "text-[var(--success)]" : "text-[var(--text-dim)]"}`}>
                  Gate {i + 1}
                </div>
                {!compact && (
                  <div className={`text-[11px] leading-tight truncate ${active ? "text-white" : done ? "text-[var(--text-muted)]" : "text-[var(--text-muted)]"}`}>
                    {GATE_LABELS[g]}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
